import { type IconDefinition } from '@/components/ui/app-icon';
import {
  iconArrowsLeftRight,
  iconBolt,
  iconBook,
  iconCircleCheck,
  iconBrain,
  iconBuilding,
  iconCircleQuestion,
  iconCoin,
  iconClock,
  iconDatabase,
  iconEnvelope,
  iconFlask,
  iconLayerGroup,
  iconLinkedin,
  iconListCheck,
  iconMagnifyingGlass,
  iconPen,
  iconPlug,
  iconReply,
  iconRobot,
  iconSitemap,
  iconSlack,
  iconToggleOn,
  iconTriangleExclamation,
} from '@/lib/icons';
import {
  CLU_TOOL_STEPS,
  INSTANTLY_ACTIONS,
} from '@/components/agent-chat/clu-tool-steps';
import { CONNECTION_APP_NAME } from '@/components/agent-chat/agent-cards.util';
import { ONE_OFF_TOOL_STEPS } from '@/components/agent-chat/one-off/one-off.util';
import { LIST_SHAPING_TOOL_STEPS } from '@/components/agent-chat/list-shaping/list-shaping.util';

/**
 * Friendly names for Clu's tools: the timeline labels every chat shows, and
 * the steps an agent's tools read as in the Gumloop style.
 */
export const TOOL_DISPLAY_NAMES: Record<string, string> = {
  tool_result: 'Tool result',
  confirmation_request: 'Confirmation',
  people_search: 'People search',
  web_people_search: 'Web search',
  import_to_list: 'Import',
  enrich_leads: 'Enrichment',
  create_smart_column: 'Smart column',
  get_lead_lists: 'Lead lists',
  create_lead_list: 'Create list',
  rename_lead_list: 'Rename list',
  delete_lead_list: 'Delete list',
  get_credit_balance: 'Credit balance',
  estimate_cost: 'Cost estimate',
  filter_list: 'Filters',
  company_search: 'Company search',
  get_company_profile: 'Reading your profile',
  get_gtm_profile: 'Reading your profile',
};

/**
 * Friendly label for a raw backend tool name. Prefers an explicit
 * {@link TOOL_DISPLAY_NAMES} entry (also after stripping an `_async` suffix),
 * otherwise de-snakes + strips a trailing "async" and sentence-cases the rest,
 * so the timeline never shows raw identifiers like "get company profile async".
 */
export function cleanToolLabel(toolName: string): string {
  const name = toolName.trim();
  if (!name) return 'Working';
  if (TOOL_DISPLAY_NAMES[name]) return TOOL_DISPLAY_NAMES[name];
  const base = name.replace(/_async$/i, '');
  if (TOOL_DISPLAY_NAMES[base]) return TOOL_DISPLAY_NAMES[base];
  const words = base
    .replace(/_/g, ' ')
    .replace(/\s*async\s*$/i, '')
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Working';
}

interface AgentToolStep {
  done: string;
  running: string;
  icon: IconDefinition;
}

/** Clu's sequence tools, and the confirmed actions they ask for (a one-off
 *  email among them). */
const SEQUENCE_TOOL_STEPS: Record<string, AgentToolStep> = {
  list_sequences: {
    done: 'Read the sequences',
    running: 'Reading the sequences',
    icon: iconEnvelope,
  },
  get_sequence: {
    done: 'Read the sequence',
    running: 'Reading the sequence',
    icon: iconEnvelope,
  },
  create_sequence: {
    done: 'Created a sequence',
    running: 'Creating a sequence',
    icon: iconEnvelope,
  },
  edit_sequence: {
    done: 'Edited the sequence',
    running: 'Editing the sequence',
    icon: iconEnvelope,
  },
  set_sequence_status: {
    done: 'Changed the sequence',
    running: 'Changing the sequence',
    icon: iconEnvelope,
  },
  enroll_in_sequence: {
    done: 'Added people to the sequence',
    running: 'Adding people to the sequence',
    icon: iconEnvelope,
  },
  // One email from the user's own mailbox, after its card.
  list_my_mailboxes: {
    done: 'Read your mailboxes',
    running: 'Reading your mailboxes',
    icon: iconEnvelope,
  },
  send_mailbox_email: {
    done: 'Sent the email',
    running: 'Sending the email',
    icon: iconEnvelope,
  },
  // The Inbox, and a reply in one of its conversations after its card.
  list_inbox_threads: {
    done: 'Read the Inbox',
    running: 'Reading the Inbox',
    icon: iconEnvelope,
  },
  read_inbox_thread: {
    done: 'Read a conversation',
    running: 'Reading a conversation',
    icon: iconEnvelope,
  },
  reply_in_thread: {
    done: 'Sent the reply',
    running: 'Sending the reply',
    icon: iconReply,
  },
  // One LinkedIn message or invitation from the user's own account.
  list_my_linkedin_accounts: {
    done: 'Read your LinkedIn accounts',
    running: 'Reading your LinkedIn accounts',
    icon: iconLinkedin,
  },
  send_linkedin_action: {
    done: 'Sent on LinkedIn',
    running: 'Sending on LinkedIn',
    icon: iconLinkedin,
  },
};

/** An agent's tools as steps: the label once done, while running, and the icon. */
export const AGENT_TOOL_STEPS: Record<string, AgentToolStep> = {
  get_agent: {
    done: 'Read the agent',
    running: 'Reading the agent',
    icon: iconRobot,
  },
  list_connections: {
    done: 'Checked connections',
    running: 'Checking connections',
    icon: iconPlug,
  },
  describe_audience_filters: {
    done: 'Read the audience filters',
    running: 'Reading the audience filters',
    icon: iconMagnifyingGlass,
  },
  lookup_filter_values: {
    done: 'Looked up filter values',
    running: 'Looking up filter values',
    icon: iconMagnifyingGlass,
  },
  size_audience: {
    done: 'Sized the audience',
    running: 'Sizing the audience',
    icon: iconMagnifyingGlass,
  },
  recall_my_conversations: {
    done: 'Searched past conversations',
    running: 'Searching past conversations',
    icon: iconBrain,
  },
  get_company_profile: {
    done: 'Read your company profile',
    running: 'Reading your company profile',
    icon: iconBuilding,
  },
  get_gtm_profile: {
    done: 'Read your company profile',
    running: 'Reading your company profile',
    icon: iconBuilding,
  },
  update_company_profile: {
    done: 'Updated your company profile',
    running: 'Updating your company profile',
    icon: iconBuilding,
  },
  describe_play_steps: {
    done: 'Read the play steps',
    running: 'Reading the play steps',
    icon: iconBook,
  },
  list_skills: {
    done: 'Read the skill library',
    running: 'Reading the skill library',
    icon: iconBook,
  },
  update_agent: {
    done: 'Updated the agent',
    running: 'Updating the agent',
    icon: iconPen,
  },
  set_schedule: {
    done: 'Set the schedule',
    running: 'Setting the schedule',
    icon: iconClock,
  },
  load_skill: {
    done: 'Read a skill',
    running: 'Reading a skill',
    icon: iconBook,
  },
  attach_skill: {
    done: 'Attached a skill',
    running: 'Attaching a skill',
    icon: iconBook,
  },
  detach_skill: {
    done: 'Removed a skill',
    running: 'Removing a skill',
    icon: iconBook,
  },
  create_skill: {
    done: 'Created a skill',
    running: 'Creating a skill',
    icon: iconBook,
  },
  create_record_skill: {
    done: 'Drafted a record skill',
    running: 'Drafting a record skill',
    icon: iconBook,
  },
  update_record_skill: {
    done: 'Drafted record skill changes',
    running: 'Drafting record skill changes',
    icon: iconBook,
  },
  finish_setup: {
    done: 'Finished setup',
    running: 'Finishing setup',
    icon: iconPen,
  },
  propose_play_changes: {
    done: 'Proposed a play change',
    running: 'Proposing a play change',
    icon: iconSitemap,
  },
  accept_play_proposal: {
    done: 'Saved the play',
    running: 'Saving the play',
    icon: iconSitemap,
  },
  discard_play_proposal: {
    done: 'Discarded the proposal',
    running: 'Discarding the proposal',
    icon: iconSitemap,
  },
  // A save of the agent's play on the canvas, told in its chat.
  save_play: {
    done: 'Saved the play on the canvas',
    running: 'Saving the play',
    icon: iconSitemap,
  },
  offer_replies: {
    done: 'Offered replies',
    running: 'Offering replies',
    icon: iconReply,
  },
  // The question card, registered as ask_questions (see TOOL_ALIASES).
  ask_user: {
    done: 'Asking for your input',
    running: 'Asking for your input',
    icon: iconCircleQuestion,
  },
  request_connection: {
    done: 'Asked to connect an app',
    running: 'Asking to connect an app',
    icon: iconPlug,
  },
  run_agent_test: {
    done: 'Started a dry run',
    running: 'Starting a dry run',
    icon: iconFlask,
  },
  // Try it (try_it_v1): Clu's Try replaces run_agent_test with the flag on.
  run_agent_sample: {
    done: 'Started Try it',
    running: 'Starting Try it',
    icon: iconFlask,
  },
  // Chats saved before get_run replaced it.
  get_latest_run: {
    done: 'Read the latest run',
    running: 'Reading the latest run',
    icon: iconFlask,
  },
  list_runs: {
    done: 'Read the runs',
    running: 'Reading the runs',
    icon: iconFlask,
  },
  get_run: {
    done: 'Read a run',
    running: 'Reading a run',
    icon: iconFlask,
  },
  get_run_records: {
    done: "Read a run's records",
    running: "Reading a run's records",
    icon: iconFlask,
  },
  request_agent_status: {
    done: 'Asked to confirm',
    running: 'Asking to confirm',
    icon: iconToggleOn,
  },
  // The confirmed status change arrives under its action's name.
  set_agent_status: {
    done: 'Changed the status',
    running: 'Changing the status',
    icon: iconToggleOn,
  },
  request_agent_limits: {
    done: 'Asked to confirm',
    running: 'Asking to confirm',
    icon: iconCoin,
  },
  set_agent_limits: {
    done: 'Changed the limits',
    running: 'Changing the limits',
    icon: iconCoin,
  },
  // A cleanup play's reads and its merge card (W5 design 8.5).
  crm_scan_status: {
    done: 'Checked the CRM scan',
    running: 'Checking the CRM scan',
    icon: iconDatabase,
  },
  find_crm_duplicates: {
    done: 'Read the duplicate groups',
    running: 'Reading the duplicate groups',
    icon: iconListCheck,
  },
  read_duplicate_group: {
    done: 'Read a duplicate group',
    running: 'Reading a duplicate group',
    icon: iconListCheck,
  },
  describe_crm_object: {
    done: 'Read the CRM fields',
    running: 'Reading the CRM fields',
    icon: iconDatabase,
  },
  load_guide: {
    done: 'Read a guide',
    running: 'Reading a guide',
    icon: iconBook,
  },
  request_merge_duplicate_groups: {
    done: 'Asked to confirm',
    running: 'Asking to confirm',
    icon: iconLayerGroup,
  },
  // The confirmed merge arrives under its action's name.
  merge_duplicate_groups: {
    done: 'Started the merge',
    running: 'Starting the merge',
    icon: iconLayerGroup,
  },
  // What a play enriches, added to the org's default CRM field mapping; the
  // confirmed change arrives under its action's name.
  request_crm_field_mappings: {
    done: 'Asked to confirm',
    running: 'Asking to confirm',
    icon: iconArrowsLeftRight,
  },
  // The org's default mapping, which every sync writes with: not just the
  // play being built (CLE-557).
  add_crm_field_mappings: {
    done: "Updated the org's default field mapping",
    running: "Updating the org's default field mapping",
    icon: iconArrowsLeftRight,
  },
  // A task or note on one CRM record; the confirmed write arrives under its
  // action's name.
  request_crm_activity: {
    done: 'Asked to confirm',
    running: 'Asking to confirm',
    icon: iconListCheck,
  },
  create_crm_activity: {
    done: 'Wrote to the CRM',
    running: 'Writing to the CRM',
    icon: iconListCheck,
  },
  // One Slack message from Clu; the confirmed post arrives under its
  // action's name.
  request_send_slack_message: {
    done: 'Asked to confirm',
    running: 'Asking to confirm',
    icon: iconSlack,
  },
  // Its card is built under this name too, so the running words stay neutral.
  send_slack_message: {
    done: 'Sent the Slack message',
    running: 'Preparing the Slack message',
    icon: iconSlack,
  },
  ...SEQUENCE_TOOL_STEPS,
  // One-off plays and live runs (one-off/one-off.util.ts).
  ...ONE_OFF_TOOL_STEPS,
  // A list's columns and skills (list-shaping/list-shaping.util.ts).
  ...LIST_SHAPING_TOOL_STEPS,
};

/**
 * Tools the dock can meet under a second name. Agno reserves `ask_user` for
 * its own pause, so the backend registers the question card's tool as
 * `ask_questions` (cleanse-api 8e28fc345); its events carry the public name
 * `ask_user` today. Either name reads as the question card.
 */
const TOOL_ALIASES: Record<string, string> = {
  ask_questions: 'ask_user',
};

/**
 * The name a tool's label, icon and details are kept under. A tool registered
 * from a private method streams under that name (`_get_credit_balance_async`),
 * so the underscores and the `_async` go.
 */
export function canonicalToolName(toolName: string): string {
  const base = toolName
    .trim()
    .replace(/^_+/, '')
    .replace(/_async$/i, '');
  return TOOL_ALIASES[base] ?? base;
}

/** An agent's tool or one of Clu's own: one table of steps for every chat. */
function agentToolStep(toolName: string): AgentToolStep | undefined {
  const name = canonicalToolName(toolName);
  return (
    AGENT_TOOL_STEPS[toolName] ??
    AGENT_TOOL_STEPS[name] ??
    CLU_TOOL_STEPS[toolName] ??
    CLU_TOOL_STEPS[name]
  );
}

const SEARCH_TOOLS = new Set([
  'people_search',
  'web_people_search',
  'company_search',
]);

/** read_connection_api by its args: no connection lists the connections,
 *  fields_of reads an object's fields, else it reads the named tool. An
 *  object named by an id ("2-1234567", "Invoice__c") reads as just fields. */
export function connectionReadLabel(
  inputArgs: Record<string, unknown>,
  running: boolean
): string | null {
  const fieldsOf =
    typeof inputArgs.fields_of === 'string' ? inputArgs.fields_of.trim() : '';
  if (fieldsOf) {
    const object = /^[A-Za-z]+( [A-Za-z]+)*$/.test(fieldsOf)
      ? `${fieldsOf} `
      : '';
    return running ? `Reading ${object}fields` : `Read ${object}fields`;
  }
  const connection =
    typeof inputArgs.connection === 'string' ? inputArgs.connection.trim() : '';
  if (!connection)
    return running ? 'Checking connections' : 'Checked connections';
  const app = CONNECTION_APP_NAME[connection.toLowerCase()];
  if (!app) return null;
  return running ? `Reading ${app}` : `Read ${app}`;
}

/** The step's label: the agent tool's own words, else the timeline's label. */
export function stepLabel(
  toolName: string,
  running = false,
  inputArgs: Record<string, unknown> = {}
): string {
  if (canonicalToolName(toolName) === 'run_agent_test' && inputArgs.proposal_id)
    return running ? 'Running the check' : 'Ran the check';
  if (canonicalToolName(toolName) === 'read_connection_api') {
    const label = connectionReadLabel(inputArgs, running);
    if (label) return label;
  }
  const step = agentToolStep(toolName);
  if (step) return running ? step.running : step.done;
  return cleanToolLabel(toolName);
}

function countOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

/** "Read 5 of 1,082 people", "Read 12 people". */
function listReadLabel(result: Record<string, unknown>): string | null {
  const read =
    countOf(result.count) ??
    (Array.isArray(result.results) ? result.results.length : null);
  if (read === null) return null;
  const total = countOf(result.total);
  const noun = (n: number) => (n === 1 ? 'person' : 'people');
  return total !== null && total > read
    ? `Read ${read.toLocaleString()} of ${total.toLocaleString()} ${noun(total)}`
    : `Read ${read.toLocaleString()} ${noun(read)}`;
}

/** A one-off email, reply or LinkedIn send that was refused, failed or may
 *  have gone out. */
const ONE_OFF_SEND_LABELS: Record<
  string,
  { failed: string; unconfirmed: string }
> = {
  send_mailbox_email: {
    failed: 'Email not sent',
    unconfirmed: 'Email delivery unconfirmed',
  },
  reply_in_thread: {
    failed: 'Reply not sent',
    unconfirmed: 'Reply delivery unconfirmed',
  },
  send_linkedin_action: {
    failed: 'Not sent on LinkedIn',
    unconfirmed: 'LinkedIn delivery unconfirmed',
  },
};

/** The counts a cleanse-api WriteResult carries when a write stopped after
 *  some of it landed (added, removed, set). */
const INSTANTLY_PARTIAL_COUNTS = ['added', 'removed', 'sent'] as const;

/** A confirmed Instantly change that was refused, landed in part, may have
 *  landed, is a job Instantly is still running, or added no one
 *  (cleanse-api WriteResult status). */
function instantlyActionLabel(
  name: string,
  result: Record<string, unknown>
): string | null {
  if (result.status === 'uncertain') return 'Instantly change unconfirmed';
  // 'failed' also covers a write that stopped after real changes landed.
  if (
    result.status === 'failed' &&
    INSTANTLY_PARTIAL_COUNTS.some((key) => (countOf(result[key]) ?? 0) > 0)
  )
    return 'Instantly changed in part';
  if (typeof result.error === 'string' && result.error)
    return 'Instantly not changed';
  if (result.status === 'pending') return 'Started in Instantly';
  if (name === 'instantly_add_leads' && result.added === 0)
    return 'No one added in Instantly';
  return null;
}

/** A confirmed connection write that didn't land, may have, or (a bulk
 *  one) landed on some records (cleanse-api connection_api write result),
 *  else null. */
function connectionWriteLabel(result: Record<string, unknown>): string | null {
  const connection =
    typeof result.connection === 'string' ? result.connection : '';
  const app = CONNECTION_APP_NAME[connection.toLowerCase()];
  const where = app ? ` in ${app}` : '';
  // A failed send is one whose answer never came back.
  if (
    result.status === 'uncertain' ||
    result.error === 'connection_write_failed'
  )
    return `Change unconfirmed${where}`;
  if (typeof result.error === 'string' && result.error)
    return `Nothing changed${where}`;
  if (result.status === 'partial') return `Changed in part${where}`;
  return null;
}

/** A finished step's label, where the result changes the words: an approved
 *  enroll that added no one says so, a Slack message, email, reply or
 *  LinkedIn send that did not go out or may have says so, an Instantly change
 *  that didn't land, landed in part, may have, is still running or added no
 *  one says so, a started skill
 *  names the skill, and a list read says how many people it read. */
export function resultStepLabel(
  toolName: string,
  result: Record<string, unknown>,
  inputArgs: Record<string, unknown> = {}
): string {
  const name = canonicalToolName(toolName);
  if (
    name === 'run_agent_test' &&
    (result.proposal_id ||
      (result.__display as { type?: string } | undefined)?.type ===
        'candidate_test')
  )
    return 'Ran the check';
  const failed = typeof result.error === 'string' && result.error.length > 0;
  // A refused, failed or unconfirmed post streams under the same name.
  if (name === 'send_slack_message' && failed)
    return result.error === 'delivery_unconfirmed'
      ? 'Slack delivery unconfirmed'
      : 'Slack message not sent';
  const send = ONE_OFF_SEND_LABELS[name];
  if (send && failed) return send.failed;
  if (send && result.sent === 'unconfirmed') return send.unconfirmed;
  if (INSTANTLY_ACTIONS.has(name)) {
    const label = instantlyActionLabel(name, result);
    if (label) return label;
  }
  if (name === 'connection_api_write') {
    const label = connectionWriteLabel(result);
    if (label) return label;
  }
  if (name === 'enroll_in_sequence' && result.added === 0)
    return 'No one added to the sequence';
  if (
    name === 'run_list_skill' &&
    !failed &&
    typeof result.column_label === 'string' &&
    result.column_label.trim()
  )
    return `Started ${result.column_label.trim()}`;
  if (name === 'get_leads' && !failed) {
    const label = listReadLabel(result);
    if (label) return label;
  }
  return stepLabel(toolName, false, inputArgs);
}

/** The step's icon (from @/lib/icons, per the spec's list); a bolt for any other tool. */
export function stepIcon(toolName: string, isError = false): IconDefinition {
  if (isError) return iconTriangleExclamation;
  const step = agentToolStep(toolName);
  if (step) return step.icon;
  if (SEARCH_TOOLS.has(toolName)) return iconMagnifyingGlass;
  if (toolName === 'confirmation') return iconCircleCheck;
  return iconBolt;
}
